import { Component, Inject, OnInit } from '@angular/core';
import { BehaviorSubject, catchError, debounceTime, filter, Observable, of, switchMap, tap } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Contragent } from '@models/contragent.model';
import { Invoice } from '@models/invoice/invoice.model';
import { becomePayerEDRPOUErrors, ControlNames, phoneErrors, PhoneMaskConfig } from './become-payer-dialog.constants';
import { ChangeEwSaveRequestData } from '@shared/components/change-ew/models/change-ew-save-request';
import { getError } from '@shared/utils/error.utils';
import { Unsubscriber } from '@shared/classes/unsubscriber';
import { InvoiceApi } from '../../../services/invoices/invoice.api';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { InvoiceApiService } from '../../../services/invoices/invoice-api.service';
import { ChangeEwService } from '../../../services/change-ew.service';
import { AlertToastService } from '@services';
import { ChangeEwSaveResponseData } from '../change-ew';

@Component({
  selector: 'app-become-payer-dialog',
  templateUrl: './become-payer-dialog.component.html',
  styleUrls: ['./become-payer-dialog.component.scss'],
})
export class BecomePayerDialogComponent extends Unsubscriber implements OnInit {

  protected readonly phoneErrors = phoneErrors;
  protected readonly EDRPOUErrors = becomePayerEDRPOUErrors;
  protected readonly getError = getError;
  protected readonly ControlNames = ControlNames;
  protected readonly PhoneMaskConfig = PhoneMaskConfig;

  payerName: string = '';
  organization: Contragent;

  phoneNumberIsLoading: boolean = false;
  EDRPOUIsLoading: boolean = false;

  loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loading$: Observable<boolean> = this.loading.asObservable();

  form: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { invoice: Invoice },
    private dialogRef: MatDialogRef<BecomePayerDialogComponent>,
    private fb: FormBuilder,
    private apiService: InvoiceApi,
    private invoiceApiService: InvoiceApiService,
    private changeEwService: ChangeEwService,
    private alertToastService: AlertToastService,
  ) {
    super();
  }

  get invoice(): Invoice | null {
    return this.data?.invoice;
  }

  get phoneControl(): FormControl {
    return this.form?.get(ControlNames.Phone) as FormControl;
  }

  get EDRPOUControl(): FormControl {
    return this.form?.get(ControlNames.EDRPOU) as FormControl;
  }

  ngOnInit(): void {
    this.initForm();
    this.initFormSubscriptions();
  }

  initFormSubscriptions(): void {
    this.subscriptions = this.phoneControl.valueChanges
      .pipe(
        debounceTime(500),
        switchMap(() => {
          const cleanedPhone = this.phoneControl.value?.toString().replace(/[+()\- ]/g, '');
          if (!this.phoneControl.valid) {
            if (this.payerName) {
              this.phoneControl.markAsTouched();
            }

            this.payerName = '';
            return of(null);
          }

          this.phoneNumberIsLoading = true;

          return this.apiService.track(this.invoice.docNumber, cleanedPhone, {
            useDefaultSystem: false,
            parseErrors: true,
          })
            .pipe(
              catchError((errors) => {
                if (errors[0]) {
                  this.phoneControl.setErrors({ notFound: true });
                  this.phoneControl.markAsTouched();

                  this.payerName = '';
                  this.phoneNumberIsLoading = false;
                  this.alertToastService.pushApiErrors(errors);
                }
                return of(null);
              }),
              tap((data) => {
                if (!data) {
                  this.phoneControl.setErrors({ notFound: true });
                }
              }),
              finalize(() => this.phoneNumberIsLoading = false),
            );
        }),
        filter((data) => !!data),
        tap((invoice: Invoice) => {
          if (invoice?.RecipientFullNameEW) {
            this.payerName = invoice.RecipientFullNameEW;
            this.EDRPOUControl.markAsUntouched();
          } else {
            this.payerName = '';
            this.phoneControl.setErrors({ notMatch: true });
            this.phoneControl.markAsTouched();
            this.EDRPOUControl.markAsUntouched();
          }
        }),
      ).subscribe();

    this.subscriptions = this.EDRPOUControl.valueChanges
      .pipe(
        tap(() => {
          if (this.EDRPOUControl.invalid) {
            this.organization = null;
          }
        }),
        filter(() => this.EDRPOUControl.valid),
        debounceTime(500),
        switchMap((edrpuo) => this.validateEDRPOU(edrpuo)),
      ).subscribe();
  }

  submit(): void {
    if (this.form.invalid || this.phoneNumberIsLoading || this.EDRPOUIsLoading) {
      this.form.markAllAsTouched();
      return;
    }

    const payload: ChangeEwSaveRequestData = {
      docNumber: this.invoice.docNumber,
      data: {
        PayerType: 'ThirdPerson',
        PaymentMethod: 'NonCash',
        CounterpartyThirdPerson: this.organization.Ref,
      },
    };

    this.loading.next(true);

    this.changeEwService.save(payload)
      .pipe(finalize(() => this.loading.next(false)))
      .subscribe(
        (response: ChangeEwSaveResponseData) => {
          this.dialogRef.close(response);
        },
        (errors) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
        });
  }

  private validateEDRPOU(value: string): Observable<Contragent | null> {
    this.EDRPOUIsLoading = true;
    return this.invoiceApiService.addByEDRPOU(value)
      .pipe(
        catchError((errors) => {
          this.organization = null;
          this.EDRPOUControl.setErrors({ notFound: true });

          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }

          return of(null);
        }),
        tap((data) => {
          if (!data || !data.Description || !data.Ref) {
            this.EDRPOUControl.setErrors({ notFound: true });
            this.EDRPOUControl.markAsTouched();
            this.organization = null;
          } else {
            this.organization = data;
          }
        }),
        finalize(() => this.EDRPOUIsLoading = false),
      );
  }

  private initForm(): void {
    this.form = this.fb.group({
      [ControlNames.Phone]: [null, [Validators.required, Validators.minLength(17)]],
      [ControlNames.EDRPOU]: [null, [Validators.required, Validators.minLength(8), Validators.maxLength(10)]],
    });
  }
}
